
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Package, Grid, List } from "lucide-react";
import { ProductForm } from "./ProductForm";
import { ProductEdit } from "./ProductEdit";
import { CreditRequestForm } from "./CreditRequestForm";
import { OrderDetailsLoadingState } from "./OrderDetailsLoadingState";
import { ProductListHeader } from "./ProductListHeader";
import { ProductListSearch } from "./ProductListSearch";
import { ProductListStats } from "./ProductListStats";
import { ProductListGrid } from "./ProductListGrid";
import { ProductListTable } from "./ProductListTable";
import { ProductListModals } from "./ProductListModals";
import { VirtualProductList } from "./VirtualProductList";
import { OptimizedOrderDetailsLoadingState } from "./OptimizedOrderDetailsLoadingState";
import { useOptimizedProductList } from "@/hooks/useOptimizedProductList";
import { useProductOperations } from "@/hooks/useProductOperations";
import { useProductFiltering } from "@/hooks/useProductFiltering";
import { Order } from "@/hooks/useOrdersManagement/types";

interface Product {
  id: string;
  barcode: string;
  name: string;
  boxes: number;
  unitsPerBox: number;
  photos: string[];
  hasCredit: boolean;
  posStatus?: 'found' | 'not-found';
  catalogName?: string;
}

interface ProductListProps {
  order: Order;
  onBack: () => void;
}

export const ProductList = ({ order, onBack }: ProductListProps) => {
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");
  const [currentView, setCurrentView] = useState<"list" | "add" | "edit" | "credit">("list");
  const [showScanner, setShowScanner] = useState(false);
  const [showPhotoCapture, setShowPhotoCapture] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);
  const [newProduct, setNewProduct] = useState({
    barcode: "",
    name: "",
    boxes: 1,
    unitsPerBox: 1,
    photos: [] as string[]
  });

  // Use our optimized hooks for better performance
  const { 
    products, 
    productsLoading, 
    productsError, 
    hasMore,
    isFetchingNextPage,
    loadMore,
    totalProducts,
    isLargeDataset,
    currentPageSize
  } = useOptimizedProductList(order.id);
  const { handleAddProduct, handleDeleteProduct, handleScanResult } = useProductOperations(order, products);
  const { 
    searchQuery, 
    setSearchQuery, 
    posStatusFilter, 
    setPosStatusFilter, 
    filteredProducts 
  } = useProductFiltering(products);

  // Show enhanced loading state with progress
  if (productsLoading) {
    const progress = products.length > 0 ? Math.min((products.length / (totalProducts || 1)) * 100, 100) : 25;
    const loadingStage = products.length > 0 
      ? `Loading products... (${products.length}/${totalProducts})`
      : "Fetching order data...";
    
    return (
      <OptimizedOrderDetailsLoadingState 
        onBack={onBack} 
        progress={progress}
        loadingStage={loadingStage}
        showProductSkeletons={products.length === 0}
      />
    );
  }

  // Show performance warning for large datasets
  if (isLargeDataset && products.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-4">
        <div className="text-center">
          <Package className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-yellow-800 mb-2">Large Order Detected</h3>
          <p className="text-sm text-yellow-700 mb-4">
            This order has {totalProducts} products. We're optimizing the loading for better performance.
          </p>
          <div className="text-xs text-yellow-600">
            Products will load in batches of 30 for optimal performance.
          </div>
        </div>
      </div>
    );
  }

  // Show error state if products failed to load
  if (productsError) {
    console.error('Products error in component:', productsError);
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
        <div className="w-full px-2 md:px-4 lg:container lg:mx-auto py-2 md:py-6">
          <Card className="border-0 shadow-sm">
            <CardContent className="text-center py-8">
              <Package className="w-12 h-12 mx-auto text-red-400 mb-3" />
              <h3 className="text-lg font-semibold text-red-600 mb-2">Error Loading Products</h3>
              <p className="text-sm text-red-500 mb-4">
                {productsError.message || "Unable to load products for this order."}
              </p>
              <div className="space-x-2">
                <Button onClick={() => window.location.reload()} variant="outline" size="sm">
                  Refresh Page
                </Button>
                <Button onClick={onBack} variant="outline" size="sm">
                  Go Back
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const handleSearchSelect = (product: Product) => {
    setSelectedProduct(product);
    setCurrentView("edit");
    setSearchQuery("");
    setShowSearchDropdown(false);
  };

  const handleProductCardClick = (product: Product) => {
    setSelectedProduct(product);
    setCurrentView("edit");
  };

  const handleImageClick = (imageUrl: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setZoomedImage(imageUrl);
  };

  const handleScanResultInternal = async (scannedBarcode: string) => {
    const productData = await handleScanResult(scannedBarcode);
    
    // Handle case where duplicate was detected and no new product should be created
    if (!productData) {
      console.log('Scan cancelled due to duplicate detection');
      setShowScanner(false);
      return;
    }
    
    setNewProduct(prev => ({ 
      ...prev, 
      ...productData
    }));
    
    // Automatically open camera after successful barcode scan
    setTimeout(() => {
      setShowPhotoCapture(true);
    }, 500);
  };

  const handlePhotoCapture = (photoUrl: string) => {
    setNewProduct(prev => ({
      ...prev,
      photos: [...prev.photos, photoUrl]
    }));
  };

  const handleAddProductInternal = async () => {
    const success = await handleAddProduct(newProduct);
    if (success) {
      setNewProduct({
        barcode: "",
        name: "",
        boxes: 1,
        unitsPerBox: 1,
        photos: []
      });
      setCurrentView("list");
    }
  };

  const handleEditProduct = (product: Product) => {
    setSelectedProduct(product);
    setCurrentView("edit");
  };

  // Show credit request form
  if (currentView === "credit") {
    return (
      <CreditRequestForm
        onBack={() => setCurrentView("list")}
        order={order}
        products={products}
      />
    );
  }

  // Show product edit view
  if (currentView === "edit" && selectedProduct) {
    return (
      <ProductEdit
        product={selectedProduct}
        orderId={order.id}
        onBack={() => setCurrentView("list")}
        onSave={() => setCurrentView("list")}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <div className="w-full px-2 md:px-4 lg:container lg:mx-auto py-2 md:py-6">
        {/* Header */}
        <ProductListHeader
          order={order}
          onBack={onBack}
          onAddProduct={() => setCurrentView("add")}
          onCreditReport={() => setCurrentView("credit")}
        />

        {/* Search Bar and Filters */}
        <ProductListSearch
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          showSearchDropdown={showSearchDropdown}
          onShowSearchDropdown={setShowSearchDropdown}
          filteredProducts={filteredProducts}
          onProductSelect={handleSearchSelect}
          posStatusFilter={posStatusFilter}
          onPosStatusFilterChange={setPosStatusFilter}
          allProducts={products}
        />

        {/* Summary Stats - Updated to use new statistics */}
        {products.length > 0 && (
          <ProductListStats 
            orderId={order.id}
            searchQuery={searchQuery}
            posStatusFilter={posStatusFilter === 'found' ? 'existing' : posStatusFilter === 'not-found' ? 'new' : 'all'}
          />
        )}

        {/* View Mode Toggle */}
        <div className="flex justify-center mb-4">
          <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
            <Button
              variant={viewMode === "grid" ? "default" : "ghost"}
              onClick={() => setViewMode("grid")}
              size="sm"
              className="h-8"
            >
              <Grid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === "table" ? "default" : "ghost"}
              onClick={() => setViewMode("table")}
              size="sm"
              className="h-8"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Add Product Form */}
        {currentView === "add" && (
          <div className="mb-6">
            <ProductForm
              barcode={newProduct.barcode}
              name={newProduct.name}
              boxes={newProduct.boxes}
              unitsPerBox={newProduct.unitsPerBox}
              photos={newProduct.photos}
              onBarcodeChange={(barcode) => setNewProduct(prev => ({ ...prev, barcode }))}
              onNameChange={(name) => setNewProduct(prev => ({ ...prev, name }))}
              onBoxesChange={(boxes) => setNewProduct(prev => ({ ...prev, boxes }))}
              onUnitsPerBoxChange={(unitsPerBox) => setNewProduct(prev => ({ ...prev, unitsPerBox }))}
              onOpenScanner={() => setShowScanner(true)}
              onOpenCamera={() => setShowPhotoCapture(true)}
              onSave={handleAddProductInternal}
              onCancel={() => setCurrentView("list")}
              isSaving={false}
            />
          </div>
        )}

        {/* Products List */}
        {filteredProducts.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="text-center py-8">
              <Package className="w-12 h-12 mx-auto text-gray-400 mb-3" />
              <h3 className="text-lg font-semibold text-gray-600 mb-2">
                {products.length === 0 ? "No Products Added" : "No Products Match Filter"}
              </h3>
              <p className="text-sm text-gray-500">
                {products.length === 0 
                  ? "Start by clicking \"Add Product\" to scan and document items."
                  : "Try adjusting your search or filter criteria."
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <VirtualProductList
            products={filteredProducts}
            viewMode={viewMode}
            hasMore={hasMore}
            isFetchingNextPage={isFetchingNextPage}
            loadMore={loadMore}
            onProductClick={handleProductCardClick}
            onEditProduct={handleEditProduct}
            onDeleteProduct={handleDeleteProduct}
            onImageClick={handleImageClick}
          />
        )}
      </div>

      {/* Modals */}
      <ProductListModals
        showScanner={showScanner}
        onScanResult={handleScanResultInternal}
        onCloseScanner={() => setShowScanner(false)}
        showPhotoCapture={showPhotoCapture}
        onPhotoCapture={handlePhotoCapture}
        onClosePhotoCapture={() => setShowPhotoCapture(false)}
        zoomedImage={zoomedImage}
        onCloseZoomedImage={() => setZoomedImage(null)}
      />
    </div>
  );
};
